/**
 * Batch #14.12 — Portail Client : module Factures (Facturation Cabinet).
 *
 * Affiche les factures cabinet déposées par l'admin (`deposited_in_portal=True`).
 * Permet au client de :
 *   - Voir la liste avec statut, total, solde
 *   - Télécharger le PDF officiel (auth Bearer via secureDownload)
 *   - Payer par carte (Stripe Checkout) si solde > 0 et Stripe configuré
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Receipt, Download, CreditCard, Loader2, CheckCircle2,
  AlertCircle, FileText, Calendar,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API, portalHeaders, secureDownload } from "../helpers.js";

const STATUS_PILL = {
  brouillon:           { bg: "bg-slate-100",   fg: "text-slate-700",   label: "Brouillon" },
  envoyee:             { bg: "bg-blue-100",    fg: "text-blue-700",    label: "Envoyée" },
  en_attente:          { bg: "bg-amber-100",   fg: "text-amber-700",   label: "En attente" },
  partiellement_payee: { bg: "bg-orange-100",  fg: "text-orange-700",  label: "Partiellement payée" },
  payee:               { bg: "bg-emerald-100", fg: "text-emerald-700", label: "Payée" },
  en_retard:           { bg: "bg-red-100",     fg: "text-red-700",     label: "En retard" },
  annulee:             { bg: "bg-gray-100",    fg: "text-gray-500",    label: "Annulée" },
};

function StatusPill({ status }) {
  const s = STATUS_PILL[status] || { bg: "bg-slate-100", fg: "text-slate-600", label: status || "—" };
  return (
    <span
      className={`inline-flex items-center gap-1 ${s.bg} ${s.fg} text-xs font-medium px-2 py-0.5 rounded-full`}
      data-testid="portal-invoice-status-pill"
    >
      {status === "payee" && <CheckCircle2 className="w-3 h-3" />}
      {s.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CA", { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

function formatMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString("fr-CA", { style: "currency", currency: "CAD" });
}

export default function FacturesTab({ primaryColor = "#072354" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null); // invoice_id en cours de paiement
  const [stripeConfigured, setStripeConfigured] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/portal/invoices`, { headers: portalHeaders() });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStripeStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/stripe/status`, { headers: portalHeaders() });
      setStripeConfigured(!!r.data?.configured);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    loadStripeStatus();
  }, [load, loadStripeStatus]);

  // Batch #14.12 — Gestion du retour Stripe : ?invoice_paid={id}&session_id=... ou ?invoice_cancel=
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paidId = params.get("invoice_paid");
    const cancelId = params.get("invoice_cancel");
    const sessionId = params.get("session_id");

    if (paidId && sessionId) {
      (async () => {
        try {
          const r = await axios.get(
            `${API}/portal/invoices/${paidId}/checkout-status?session_id=${encodeURIComponent(sessionId)}`,
            { headers: portalHeaders() },
          );
          if (r.data?.paid) {
            toast.success("Paiement reçu — Merci !");
          } else {
            toast.info("Paiement en attente de confirmation");
          }
        } catch { /* silent */ }
        // Nettoie les query params pour ne pas re-trigger au refresh
        window.history.replaceState({}, "", window.location.pathname);
        load();
      })();
    } else if (cancelId) {
      toast.info("Paiement annulé — vous pouvez réessayer.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [load]);

  const handleDownload = async (inv) => {
    const filename = `Facture-${inv.invoice_number || inv.id}.pdf`;
    await secureDownload(`/api/portal/invoices/${inv.id}/pdf`, filename);
  };

  const handlePay = async (inv) => {
    setPaying(inv.id);
    try {
      const origin_url = window.location.origin;
      const r = await axios.post(
        `${API}/portal/invoices/${inv.id}/stripe-checkout`,
        { origin_url },
        { headers: portalHeaders() },
      );
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Lien de paiement indisponible");
        setPaying(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Stripe");
      setPaying(null);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-gray-500 flex items-center gap-2" data-testid="portal-factures-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement de vos factures…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="portal-factures-empty">
        <Receipt className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <p className="text-sm text-slate-500">
          Aucune facture déposée pour le moment.<br />
          Vos factures cabinet apparaîtront ici dès que votre conseiller les aura déposées.
        </p>
      </div>
    );
  }

  const totalDue = items
    .filter((i) => !i.is_paid)
    .reduce((acc, i) => acc + Number(i.balance || 0), 0);

  return (
    <div className="space-y-4" data-testid="portal-factures-list">
      {totalDue > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-amber-900" data-testid="portal-factures-due-banner">
          <AlertCircle className="w-4 h-4" />
          <span>
            <strong>{formatMoney(totalDue)}</strong> en attente de paiement sur {items.filter((i) => !i.is_paid).length} facture(s)
          </span>
        </div>
      )}

      {items.map((inv) => {
        const balance = Number(inv.balance || 0);
        const showPay = !inv.is_paid && balance > 0 && stripeConfigured;
        return (
          <div
            key={inv.id}
            className="bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md transition"
            data-testid={`portal-invoice-row-${inv.id}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: `${primaryColor}15` }}
              >
                <FileText className="w-5 h-5" style={{ color: primaryColor }} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-semibold text-gray-900 text-sm">
                    Facture #{inv.invoice_number}
                  </h3>
                  <StatusPill status={inv.status} />
                  {inv.tax_year && (
                    <span className="text-xs text-slate-500">Année {inv.tax_year}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Émise le {formatDate(inv.issue_date)}
                  </span>
                  {inv.due_date && (
                    <span>Échéance {formatDate(inv.due_date)}</span>
                  )}
                  <span>{inv.items_count || 0} ligne(s)</span>
                </div>
              </div>

              <div className="text-right shrink-0">
                <div className="text-lg font-bold text-gray-900" data-testid={`portal-invoice-total-${inv.id}`}>
                  {formatMoney(inv.total)}
                </div>
                {!inv.is_paid && balance > 0 && (
                  <div className="text-xs text-amber-700">
                    Solde {formatMoney(balance)}
                  </div>
                )}
                {inv.is_paid && (
                  <div className="text-xs text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Payée
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
              <button
                onClick={() => handleDownload(inv)}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5"
                data-testid={`portal-invoice-download-${inv.id}`}
              >
                <Download className="w-4 h-4" /> Télécharger le PDF
              </button>

              {showPay && (
                <button
                  onClick={() => handlePay(inv)}
                  disabled={paying === inv.id}
                  className="px-3 py-1.5 rounded-lg text-white text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: primaryColor }}
                  data-testid={`portal-invoice-pay-${inv.id}`}
                >
                  {paying === inv.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  {paying === inv.id ? "Redirection…" : `Payer par carte (${formatMoney(balance)})`}
                </button>
              )}

              {!inv.is_paid && balance > 0 && !stripeConfigured && (
                <span className="text-xs text-slate-400 italic px-2 py-1" data-testid={`portal-invoice-stripe-disabled-${inv.id}`}>
                  Paiement par carte non disponible — contactez votre cabinet pour régler.
                </span>
              )}
            </div>

            {inv.notes && (
              <p className="text-xs text-slate-500 italic mt-2 pl-12" data-testid={`portal-invoice-notes-${inv.id}`}>
                {inv.notes}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
